import { useTranslation } from 'react-i18next';
import { Seo } from '../components/Seo';
import { LocalizedLink } from '../components/LocalizedLink';
import { ProductCard } from '../components/ProductCard';
import { company } from '../data/company';
import { categories, products } from '../data/products';

const APPLICATIONS = [
  { icon: '🛩️', key: 'uav' },
  { icon: '🚗', key: 'autonomous' },
  { icon: '⛽', key: 'oilGas' },
  { icon: '🏗️', key: 'tunnel' },
  { icon: '🛡️', key: 'defense' },
  { icon: '🔬', key: 'research' }
];

const ADVANTAGES = [
  { icon: '🏭', key: 'manufacturing' },
  { icon: '🎯', key: 'calibration' },
  { icon: '✅', key: 'iso' },
  { icon: '🌍', key: 'export' }
];

const STATS = [
  { num: '60+', labelKey: 'home:stats.models' },
  { num: '30+', labelKey: 'home:stats.countries' },
  { num: '15+', labelKey: 'home:stats.years' },
  { num: 'ISO', labelKey: 'home:stats.certified' }
];

export function Home() {
  const { t } = useTranslation();

  return (
    <>
      <Seo
        title="GyroNavi - Excellence in Inertial Navigation Solutions | Xi'an GyroNavi Technology Co., Ltd."
        description="Xi'an GyroNavi Technology - Professional manufacturer of fiber optic gyroscopes, quartz accelerometers, MEMS IMU and inertial navigation systems for defense, UAV, autonomous driving and industrial applications."
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: company.name,
          alternateName: company.brand,
          url: `https://${company.domain}`,
          email: company.email,
          foundingDate: String(company.founded)
        }}
      />

      {/* Hero */}
      <section className="bg-ink text-white">
        <div className="section flex min-h-[70vh] items-center">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
              {t('home:hero.title')}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{t('home:hero.subtitle')}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <LocalizedLink className="btn-primary" to="/products">
                {t('home:hero.ctaProducts')}
              </LocalizedLink>
              <LocalizedLink className="btn-secondary border-white/20 bg-white/10 text-white hover:bg-white hover:text-ink" to="/contact">
                {t('home:hero.ctaQuote')}
              </LocalizedLink>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.labelKey}>
                  <div className="text-3xl font-black text-aqua">{s.num}</div>
                  <div className="mt-1 text-sm text-slate-300">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="section">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-black text-ink">{t('home:coreProducts.title')}</h2>
          <p className="mt-2 text-slate-600">{t('home:coreProducts.subtitle')}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((cat) => (
            <LocalizedLink key={cat.slug} to={`/products/${cat.slug}`} className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-panel">
              <div className="flex h-40 items-center justify-center bg-slate-50 p-4">
                <img src={cat.image} alt={t(`products:categories.${cat.slug}.title`)} loading="lazy" className="max-h-full object-contain" />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-ink group-hover:text-aqua">{t(`products:categories.${cat.slug}.title`)}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{t(`products:categories.${cat.slug}.summary`)}</p>
                <span className="mt-4 inline-block font-semibold text-signal">{t('common:actions.viewProducts')}</span>
              </div>
            </LocalizedLink>
          ))}
        </div>
      </section>

      {/* Applications */}
      <section className="bg-slate-100">
        <div className="section">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-black text-ink">{t('home:applications.title')}</h2>
            <p className="mt-2 text-slate-600">{t('home:applications.subtitle')}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {APPLICATIONS.map((app) => (
              <LocalizedLink key={app.key} to="/applications" className="flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-5 transition hover:-translate-y-1 hover:shadow-panel">
                <div className="text-2xl">{app.icon}</div>
                <div>
                  <h3 className="font-bold text-ink">{t(`applications:items.${app.key}.title`)}</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{t(`applications:items.${app.key}.text`)}</p>
                </div>
              </LocalizedLink>
            ))}
          </div>
        </div>
      </section>

      {/* Company Advantages */}
      <section className="section">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-black text-ink">{t('home:advantages.title')}</h2>
          <p className="mt-2 text-slate-600">{t('home:advantages.subtitle')}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ADVANTAGES.map((adv) => (
            <div key={adv.key} className="rounded-lg border border-slate-200 bg-white p-6 text-center">
              <div className="text-3xl">{adv.icon}</div>
              <h3 className="mt-3 font-bold text-ink">{t(`home:advantages.items.${adv.key}.title`)}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{t(`home:advantages.items.${adv.key}.text`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured products preview */}
      <section className="bg-slate-100">
        <div className="section">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-black text-ink">{t('home:featured.title')}</h2>
            <p className="mt-2 text-slate-600">{t('home:featured.subtitle')}</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-signal py-16 text-center text-white">
        <div className="section">
          <h2 className="text-3xl font-black">{t('home:cta.title')}</h2>
          <p className="mx-auto mt-3 max-w-xl text-orange-50">{t('home:cta.text')}</p>
          <LocalizedLink to="/contact" className="btn-primary mt-6 bg-white text-signal hover:bg-orange-50">
            {t('home:cta.button')}
          </LocalizedLink>
        </div>
      </section>
    </>
  );
}
